import React, { useRef } from 'react';
import { string, func, number } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import useLiveSearch from './useLiveSearch';
import css from './LiveSearchBar.module.css';

/**
 * LiveSearchBar — instant search dropdown with debounce.
 *
 * Shows live listing results as the user types.
 * Uses /api/listings (listingsApi feature) or a custom search function.
 *
 * Usage (replace or augment TopbarSearchForm):
 *   {features.liveSearch && (
 *     <LiveSearchBar
 *       onSelect={listing => history.push(`/l/${listing.attributes.title}/${listing.id.uuid}`)}
 *       placeholder="Search listings..."
 *     />
 *   )}
 *
 * @param {Function} [props.onSelect] — called with listing object when user clicks a result
 * @param {Function} [props.onSearch] — custom search function (optional)
 * @param {string}   [props.placeholder]
 * @param {number}   [props.debounceMs]
 */
const LiveSearchBar = ({
  onSelect,
  onSearch,
  placeholder,
  debounceMs,
  className,
}) => {
  const inputRef = useRef(null);
  const { query, setQuery, results, isLoading, clearResults } = useLiveSearch({
    debounceMs,
    onSearch,
  });

  const handleSelect = (listing) => {
    clearResults();
    onSelect && onSelect(listing);
    inputRef.current?.blur();
  };

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.inputRow}>
        <svg className={css.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={placeholder}
          className={css.input}
          autoComplete="off"
        />
        {query && (
          <button type="button" onClick={clearResults} className={css.clearBtn}>✕</button>
        )}
      </div>

      {(results.length > 0 || isLoading) && (
        <div className={css.dropdown}>
          {isLoading ? (
            <p className={css.loading}>
              <FormattedMessage id="LiveSearchBar.searching" />
            </p>
          ) : (
            results.map(listing => (
              <div
                key={listing.id?.uuid || listing.id}
                className={css.result}
                onClick={() => handleSelect(listing)}
                role="option"
              >
                <span className={css.resultTitle}>
                  {listing.attributes?.title || listing.title}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

LiveSearchBar.defaultProps = {
  onSelect: null,
  onSearch: null,
  placeholder: 'Search...',
  debounceMs: 300,
  className: null,
};

LiveSearchBar.propTypes = {
  onSelect: func,
  onSearch: func,
  placeholder: string,
  debounceMs: number,
  className: string,
};

export default LiveSearchBar;
